import React from "react"
import { useEffect, useRef, useState } from "react"
import { Link } from "react-router-dom"
import { api } from "../api/client"
import { useLanguage } from "../context/LanguageContext"
import ParticleBackground from "../components/ParticleBackground"
import ActivitiesScene from "../components/ActivitiesScene"

const PAGE_SIZE = 10

const FILTER_OPTIONS = [
  { value: "all", labelKey: "allActivities" },
  { value: "lesson_completed", labelKey: "completedLessons" },
  { value: "lesson_started", labelKey: "startedLessons" },
  { value: "vocabulary_reviewed", labelKey: "vocabularyFilter" },
  { value: "profile_updated", labelKey: "profileUpdatesFilter" },
  { value: "login", labelKey: "logins" },
]

function formatActivityTypeFallback(type) {
  if (!type) return ""
  return type
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ")
}

function activityTypeLabel(type, t) {
  const map = {
    lesson_started: t("activityTypeLessonStarted"),
    lesson_completed: t("activityTypeLessonCompleted"),
    profile_updated: t("activityTypeProfileUpdated"),
    login: t("activityTypeLogin"),
    writing_practice: t("activityTypeWritingPractice"),
    reading_practice: t("activityTypeReadingPractice"),
    speaking_practice: t("activityTypeSpeakingPractice"),
    listening_practice: t("activityTypeListeningPractice"),
    vocabulary_saved: t("activityTypeVocabularySaved"),
    vocabulary_reviewed: t("activityTypeVocabularyReviewed"),
    vocabulary_quiz: t("activityTypeVocabularyQuiz"),
  }
  return map[type] || formatActivityTypeFallback(type) || t("activityTypeDefault")
}

function formatActivityDate(iso) {
  if (!iso) return "—"
  try {
    return new Date(iso).toLocaleString(undefined, {
      weekday: "short",
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    })
  } catch {
    return "—"
  }
}

function streakTierKey(streak) {
  if (streak >= 30) return "master"
  if (streak >= 14) return "advanced"
  if (streak >= 7) return "good"
  return "gettingStarted"
}

function streakCardClass(streak) {
  if (streak >= 30) return "activities-streak activities-streak--master"
  if (streak >= 14) return "activities-streak activities-streak--advanced"
  if (streak >= 7) return "activities-streak activities-streak--good"
  return "activities-streak activities-streak--start"
}

export default function Activities() {
  const { t } = useLanguage()
  const [list, setList] = useState([])
  const [stats, setStats] = useState(null)
  const [error, setError] = useState("")
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState("all")
  const [selectedActivity, setSelectedActivity] = useState(null)
  const [page, setPage] = useState(1)
  const [hasNextPage, setHasNextPage] = useState(false)
  const shapesRef = useRef(null)

  useEffect(() => {
    let mounted = true
    setLoading(true)
    setError("")
    const activityType = filter === "all" ? undefined : filter
    const offset = (page - 1) * PAGE_SIZE
    Promise.all([api.activityStats(), api.activities(PAGE_SIZE, activityType, offset)])
      .then(([s, l]) => {
        if (!mounted) return
        setStats(s)
        setList(Array.isArray(l) ? l : [])
        setHasNextPage(Array.isArray(l) && l.length === PAGE_SIZE)
      })
      .catch((e) => mounted && setError(e.message))
      .finally(() => mounted && setLoading(false))
    return () => {
      mounted = false
    }
  }, [filter, page])

  const filteredActivities = list

  if (loading && !stats) {
    return (
      <div className="min-h-screen bg-background relative overflow-hidden">
        <ParticleBackground particleCount={22} className="z-0 opacity-12 dark:opacity-8" />
        <ActivitiesScene layerRef={shapesRef} />
        <div className="activities-ambient" aria-hidden />
        <div className="relative z-10 activities-scene max-w-6xl mx-auto px-3 sm:px-6 lg:px-8 py-8 sm:py-10 lg:py-14 w-full min-w-0">
          <div className="h-9 sm:h-10 w-40 sm:w-56 rounded-xl bg-muted/70 mb-6" />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-28 sm:h-32 rounded-2xl bg-muted/50 border border-border/50" />
            ))}
          </div>
          <div className="h-32 sm:h-40 rounded-2xl bg-muted/40 border border-border/40 mb-4" />
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-20 sm:h-24 rounded-2xl bg-muted/40 border border-border/40" />
            ))}
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background relative overflow-hidden flex items-center justify-center p-4">
        <ParticleBackground particleCount={22} className="z-0 opacity-12 dark:opacity-8" />
        <ActivitiesScene layerRef={shapesRef} />
        <div className="relative z-10 w-full max-w-md rounded-2xl border border-destructive/30 bg-card/95 backdrop-blur-xl p-6 sm:p-8 text-center shadow-xl activities-panel-3d">
          <p className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground mb-3">
            {t("somethingWentWrong")}
          </p>
          <p className="text-sm text-destructive mb-8 leading-relaxed break-words">{error}</p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="w-full py-3 rounded-xl bg-destructive text-destructive-foreground text-sm font-semibold hover:opacity-90 transition-opacity"
          >
            {t("tryAgain")}
          </button>
        </div>
      </div>
    )
  }

  const totalBarPct = stats ? Math.min((stats.total_activities / 100) * 100, 100) : 0

  return (
    <div className="min-h-screen bg-background text-foreground relative overflow-hidden">
      <ParticleBackground particleCount={22} className="z-0 opacity-12 dark:opacity-8" />
      <ActivitiesScene layerRef={shapesRef} />
      <div className="activities-ambient" aria-hidden />

      <div className="relative z-10 activities-scene max-w-6xl mx-auto px-3 sm:px-6 lg:px-8 py-8 sm:py-10 lg:py-14 w-full min-w-0">
        <header className="activities-hero-minimal mb-6 md:mb-8">
          <h1 className="activities-hero-title">{t("activities")}</h1>
          <div className="activities-hero-accent" aria-hidden />
        </header>

        {stats && (
          <section className="mb-8 sm:mb-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            <div className="activities-panel-3d rounded-2xl border border-border/80 bg-card/75 dark:bg-card/55 backdrop-blur-xl p-4 sm:p-6 shadow-sm futuristic-nav-border">
              <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground mb-3 sm:mb-4">
                {t("totalActivities")}
              </p>
              <p className="text-3xl sm:text-4xl font-bold text-primary tabular-nums tracking-tight">
                {stats.total_activities}
              </p>
              <div className="mt-4 sm:mt-5 h-1.5 w-full overflow-hidden rounded-full bg-muted/80">
                <div
                  className="h-full rounded-full bg-gradient-to-r from-primary via-primary/80 to-accent transition-all duration-700 ease-out"
                  style={{ width: `${totalBarPct}%` }}
                />
              </div>
            </div>

            <div
              className={`activities-panel-3d rounded-2xl border backdrop-blur-xl p-4 sm:p-6 shadow-sm ${streakCardClass(stats.learning_streak)}`}
            >
              <p className="text-[10px] font-semibold uppercase tracking-[0.14em] opacity-85 mb-3 sm:mb-4">
                {t("dayStreak")}
              </p>
              <p className="text-3xl sm:text-4xl font-bold tabular-nums tracking-tight">
                {stats.learning_streak}
              </p>
              <p className="mt-3 sm:mt-4 inline-flex rounded-full border border-current/25 px-3 py-1 text-xs font-semibold">
                {t(streakTierKey(stats.learning_streak))}
              </p>
            </div>

            <div className="activities-panel-3d rounded-2xl border border-border/80 bg-card/75 dark:bg-card/55 backdrop-blur-xl p-4 sm:p-6 shadow-sm">
              <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground mb-3 sm:mb-4">
                {t("completed")}
              </p>
              <p className="text-3xl sm:text-4xl font-bold text-accent tabular-nums tracking-tight">
                {stats.activity_breakdown?.lesson_completed ?? 0}
              </p>
              <p className="mt-3 sm:mt-4 text-xs text-muted-foreground leading-snug">
                <span className="text-foreground/90 font-medium tabular-nums">
                  {stats.activity_breakdown?.lesson_started ?? 0}
                </span>{" "}
                {t("lessonsStarted")}
              </p>
            </div>

            <div className="activities-panel-3d rounded-2xl border border-border/80 bg-card/75 dark:bg-card/55 backdrop-blur-xl p-4 sm:p-6 shadow-sm">
              <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground mb-3 sm:mb-4">
                {t("vocabulary")}
              </p>
              <p className="text-3xl sm:text-4xl font-bold tabular-nums tracking-tight text-foreground">
                {stats.activity_breakdown?.vocabulary_reviewed ?? 0}
              </p>
              <p className="mt-3 sm:mt-4 text-xs text-muted-foreground leading-snug">
                <span className="text-foreground/90 font-medium tabular-nums">
                  {stats.activity_breakdown?.vocabulary_saved ?? 0}
                </span>{" "}
                {t("activityTypeVocabularySaved").toLowerCase()}
              </p>
            </div>
          </section>
        )}

        <section className="mb-6 flex flex-col gap-4 sm:gap-5 lg:flex-row lg:items-end lg:justify-between">
          <div className="min-w-0">
            <h2 className="text-xl sm:text-2xl font-bold tracking-tight">{t("recentActivities")}</h2>
            <p className="text-sm text-muted-foreground mt-2 max-w-xl leading-relaxed">
              {t("latestLearningMilestones")}
            </p>
          </div>
          <div className="flex flex-wrap gap-2" role="group" aria-label={t("allActivities")}>
            {FILTER_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => {
                  setFilter(opt.value)
                  setSelectedActivity(null)
                  setPage(1)
                }}
                className={`rounded-full px-3 sm:px-3.5 py-2 text-xs sm:text-sm font-medium transition-all duration-200 ${
                  filter === opt.value
                    ? "bg-primary text-primary-foreground shadow-md shadow-primary/20 ring-1 ring-primary/30"
                    : "border border-border/80 bg-card/60 text-muted-foreground hover:border-primary/35 hover:text-foreground hover:bg-card/90"
                }`}
              >
                {t(opt.labelKey)}
              </button>
            ))}
          </div>
        </section>

        <section className="space-y-3 sm:space-y-4">
          {filteredActivities.length === 0 ? (
            <div className="activities-panel-3d rounded-2xl border border-dashed border-border/80 bg-card/35 dark:bg-card/25 backdrop-blur-sm px-4 sm:px-6 py-14 sm:py-20 text-center">
              <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground mb-3">
                {t("recentActivities")}
              </p>
              <h3 className="text-lg font-semibold text-foreground mb-2">{t("noActivitiesFound")}</h3>
              <p className="text-sm text-muted-foreground max-w-md mx-auto leading-relaxed">
                {t("tryChangingFilter")}
              </p>
            </div>
          ) : (
            filteredActivities.map((activity) => {
              const expanded = selectedActivity?.id === activity.id
              return (
                <article
                  key={activity.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => setSelectedActivity(expanded ? null : activity)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault()
                      setSelectedActivity(expanded ? null : activity)
                    }
                  }}
                  className={`activities-panel-3d rounded-2xl border bg-card/70 dark:bg-card/50 backdrop-blur-xl p-4 sm:p-5 text-left transition-all duration-200 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/45 focus-visible:ring-offset-2 focus-visible:ring-offset-background w-full min-w-0 ${
                    expanded
                      ? "border-primary/45 shadow-lg shadow-primary/[0.07]"
                      : "border-border/65 hover:border-primary/28 hover:shadow-md hover:shadow-black/[0.03] dark:hover:shadow-black/20"
                  }`}
                >
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-6">
                    <div className="min-w-0 flex-1 space-y-2">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="inline-flex rounded-md border border-primary/30 bg-primary/[0.09] px-2.5 py-1 text-[11px] font-bold uppercase tracking-[0.08em] text-primary">
                          {activityTypeLabel(activity.activity_type, t)}
                        </span>
                        {expanded && (
                          <span className="text-[10px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
                            {t("activityDetails")}
                          </span>
                        )}
                      </div>
                      <p className="text-sm sm:text-[0.9375rem] text-foreground/90 leading-relaxed break-words">
                        {activity.description}
                      </p>
                      <time
                        className="block text-xs text-muted-foreground tabular-nums"
                        dateTime={activity.created_at}
                      >
                        {formatActivityDate(activity.created_at)}
                      </time>
                    </div>
                    <div className="shrink-0 sm:pt-0.5 text-left sm:text-right">
                      <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                        {expanded ? t("collapseActivity") : t("expandActivity")}
                      </span>
                    </div>
                  </div>

                  {expanded && (
                    <div className="mt-5 pt-5 border-t border-border/60 grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-5 text-sm">
                      <div className="min-w-0">
                        <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground mb-1.5">
                          {t("activityId")}
                        </p>
                        <p className="font-mono text-xs break-all text-foreground/90">{activity.id}</p>
                      </div>
                      <div>
                        <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground mb-1.5">
                          {t("type")}
                        </p>
                        <p className="capitalize text-foreground/90">
                          {activity.activity_type.replace(/_/g, " ")}
                        </p>
                      </div>
                      <div>
                        <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground mb-1.5">
                          {t("timestamp")}
                        </p>
                        <p className="tabular-nums text-xs text-foreground/90">
                          {new Date(activity.created_at).toLocaleTimeString()}
                        </p>
                      </div>
                    </div>
                  )}
                </article>
              )
            })
          )}
        </section>

        {filteredActivities.length > 0 && (
          <div className="activities-panel-3d mt-8 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 rounded-2xl border border-border/70 bg-card/60 dark:bg-card/45 backdrop-blur-xl px-4 py-3">
            <p className="text-xs sm:text-sm text-muted-foreground tabular-nums text-center sm:text-left">
              {t("pageLabel")}{" "}
              <span className="text-foreground font-semibold">{page}</span>
            </p>
            <div className="flex gap-2 justify-center sm:justify-end">
              <button
                type="button"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="flex-1 sm:flex-none rounded-xl border border-border/80 bg-background/70 px-4 py-2 text-xs sm:text-sm font-semibold text-foreground hover:border-primary/35 disabled:opacity-40 disabled:pointer-events-none transition-colors"
              >
                {t("previous")}
              </button>
              <button
                type="button"
                onClick={() => setPage((p) => p + 1)}
                disabled={!hasNextPage}
                className="flex-1 sm:flex-none rounded-xl border border-border/80 bg-background/70 px-4 py-2 text-xs sm:text-sm font-semibold text-foreground hover:border-primary/35 disabled:opacity-40 disabled:pointer-events-none transition-colors"
              >
                {t("next")}
              </button>
            </div>
          </div>
        )}

        <footer className="activities-panel-3d mt-10 sm:mt-12 rounded-2xl border border-border/70 bg-gradient-to-br from-primary/[0.12] via-card/85 to-accent/[0.1] dark:from-primary/[0.08] dark:via-card/60 dark:to-accent/[0.06] backdrop-blur-xl p-6 sm:p-10 text-center sm:text-left shadow-sm">
          <h3 className="text-lg sm:text-xl font-bold tracking-tight mb-2">{t("readyForMore")}</h3>
          <p className="text-sm text-muted-foreground mb-6 sm:mb-8 max-w-lg mx-auto sm:mx-0 leading-relaxed">
            {t("continueLearningJourney")}
          </p>
          <Link
            to="/dashboard"
            viewTransition
            className="inline-flex w-full sm:w-auto min-h-[2.75rem] items-center justify-center rounded-xl bg-gradient-to-r from-primary to-accent px-8 text-sm font-semibold text-primary-foreground shadow-lg shadow-primary/25 hover:opacity-[0.96] active:opacity-90 transition-opacity"
          >
            {t("exploreMoreActivities")}
          </Link>
        </footer>
      </div>
    </div>
  )
}
